// « Envois » : les saisies gardées sur ce téléphone (carnet de chantier, incrément 6 — conception § 7.2).
// Deux listes : « Envois refusés » (motif du serveur ; actions Corriger, Renvoyer, Abandonner) et « En attente d'envoi » (elles partent
// seules au retour du réseau ; « Envoyer maintenant »). Rien n'est perdu en silence : une saisie refusée reste ici jusqu'à ce que la
// personne la corrige, la renvoie ou l'abandonne (confirmé). Téléphone d'abord : l'action principale est en tête, visible sans défiler.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CloudOff, CloudUpload, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { abandonner, remettreEnAttente, saisies, synchroniser, useFileEnvoi, type OperationFile, type TypeOperation } from '../../../lib/fileEnvoi.ts';
import { useEnLigne } from '../../../lib/preferences.ts';
import { Alerte, Badge, Bouton, Champ, ChampTexte, Dialogue, EtatVide, Panneau, useConfirmation, useNotifier } from '../../../components/ui.tsx';
import { OngletsInterventions } from './commun.tsx';
import { jourHeure } from './Journal.tsx';

const NOMS: Record<TypeOperation, string> = {
  coche: 'Coche', annulation: 'Annulation de coche', temps: 'Temps passé', releve: 'Consigne de relève', reserve: 'Réserve', levee: 'Levée de réserve',
  mesure: 'Mesure', piece: 'Pièce posée', photo: 'Photo', bon: 'Bon d’intervention',
};

/** Champs corrigeables de chaque sorte de saisie (les autres — étape visée, rattachement — ne changent pas). */
type ChampCorrigeable = { cle: string; libelle: string; zone?: boolean; nombre?: boolean };
const CORRIGEABLES: Partial<Record<TypeOperation, ChampCorrigeable[]>> = {
  coche: [{ cle: 'quantite', libelle: 'Quantité faite (vide = étape faite)', nombre: true }, { cle: 'note', libelle: 'Note', zone: true },
    { cle: 'sansPhotoMotif', libelle: 'Pas de photo ? Motif (5 caractères au moins)', zone: true }],
  annulation: [{ cle: 'motif', libelle: 'Motif de l’annulation', zone: true }],
  temps: [{ cle: 'heures', libelle: 'Heures passées', nombre: true }],
  releve: [{ cle: 'texte', libelle: 'Consigne', zone: true }],
  reserve: [{ cle: 'texte', libelle: 'Ce qui ne va pas', zone: true }],
  levee: [{ cle: 'note', libelle: 'Comment elle a été levée', zone: true }],
  mesure: [{ cle: 'point', libelle: 'Ce qui est mesuré' }, { cle: 'valeur', libelle: 'Valeur', nombre: true }, { cle: 'unite', libelle: 'Unité (obligatoire)' }],
  piece: [{ cle: 'libelle', libelle: 'Désignation' }, { cle: 'numeroSerie', libelle: 'Numéro de série' }, { cle: 'reference', libelle: 'Référence' },
    { cle: 'fabricant', libelle: 'Fabricant' }],
};

function DialogueCorriger({ op, fermer, valider }: { op: OperationFile; fermer: () => void; valider: (donnees: Record<string, unknown>) => Promise<void> }) {
  const champs = CORRIGEABLES[op.type] ?? [];
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(champs.map((c) => [c.cle, op.donnees[c.cle] == null ? '' : String(op.donnees[c.cle])])));
  const [envoi, setEnvoi] = useState(false);
  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const donnees = { ...op.donnees };
      for (const c of champs) donnees[c.cle] = v[c.cle].trim() === '' ? null : v[c.cle];
      await valider(donnees);
    } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre={`Corriger — ${NOMS[op.type]}`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Fermer</Bouton><Bouton variante="principal" chargement={envoi} onClick={enregistrer}>Corriger et renvoyer</Bouton></>}>
      <div className="pile">
        {op.refus && <Alerte ton="danger" titre="Motif du refus">{op.refus.message}</Alerte>}
        <p className="petit secondaire" style={{ margin: 0 }}>{op.libelle} — {op.chantierLibelle}. Saisie le {jourHeure(op.occurredAt)} (heure du téléphone, conservée).</p>
        {champs.map((c) => (c.zone
          ? <ChampTexte key={c.cle} libelle={c.libelle} rows={2} value={v[c.cle]} onChange={(e) => setV({ ...v, [c.cle]: e.target.value })} maxLength={2000} />
          : <Champ key={c.cle} libelle={c.libelle} inputMode={c.nombre ? 'decimal' : undefined} value={v[c.cle]} onChange={(e) => setV({ ...v, [c.cle]: e.target.value })} maxLength={300} />))}
      </div>
    </Dialogue>
  );
}

function CarteEnvoi({ op, actions }: { op: OperationFile; actions?: ReactNode }) {
  return (
    <li className={`ev-carte ${op.etat === 'refusee' ? 'ev-refusee' : ''}`}>
      <div className="ev-texte">
        <strong className="ev-libelle">{op.libelle}</strong>
        <span className="petit secondaire ev-meta">
          <Badge ton={op.etat === 'refusee' ? 'danger' : 'info'} icone={op.etat === 'refusee' ? <AlertTriangle size={14} aria-hidden /> : <CloudUpload size={14} aria-hidden />}>
            {op.etat === 'refusee' ? 'Refusée' : 'En attente'}</Badge>
          {' '}saisie le {jourHeure(op.occurredAt)} — {op.chantierLibelle}
          {op.etat === 'en_attente' && op.tentatives > 0 ? ` — ${op.tentatives} essai${op.tentatives > 1 ? 's' : ''}` : ''}
        </span>
        {/* Sur téléphone, le motif tient sur trois lignes (en entier dans « Corriger ») : les actions restent visibles sans défiler. */}
        {op.refus && <span className="petit ev-motif" title={op.refus.message}><strong>Motif : </strong>{op.refus.message}</span>}
      </div>
      {actions && <div className="ev-actions">{actions}</div>}
    </li>
  );
}

export function PageEnvois() {
  const { proprio, enAttente, refusees, lue } = useFileEnvoi();
  const enLigne = useEnLigne();
  const notifier = useNotifier();
  const qc = useQueryClient();
  const confirmation = useConfirmation();
  const [envoi, setEnvoi] = useState(false);
  const [corriger, setCorriger] = useState<OperationFile | null>(null);

  const envoyer = async () => {
    setEnvoi(true);
    try {
      const b = await synchroniser(proprio);
      if (b.envoyees) {
        notifier(`${saisies(b.envoyees)} envoyée${b.envoyees > 1 ? 's' : ''} au serveur.`);
        await Promise.all(['ma-journee', 'chantier', 'chantier-jour', 'chantier-reserves', 'chantier-pieces', 'chantier-avancement', 'bon', 'bons'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      }
      if (b.refusees) notifier(`${saisies(b.refusees)} refusée${b.refusees > 1 ? 's' : ''} : voir le motif ci-dessous.`, true);
      if (b.arret === 'reseau') notifier('Pas de réseau : les saisies restent sur ce téléphone.', true);
      else if (b.arret === 'session') notifier('Session expirée : reconnectez-vous, les saisies partiront ensuite.', true);
      else if (b.arret === 'serveur') notifier('Le serveur n’a pas tout reçu : nouvel essai automatique dans une minute.', true);
    } finally { setEnvoi(false); }
  };
  const renvoyer = async (op: OperationFile, donnees?: Record<string, unknown>) => {
    await remettreEnAttente(proprio, op.clientOpId, donnees);
    setCorriger(null);
    if (navigator.onLine) await envoyer(); else notifier('Remise en attente : elle partira au retour du réseau.');
  };
  const jeter = async (op: OperationFile) => {
    const ok = await confirmation.confirmer('Abandonner cette saisie ?',
      `« ${op.libelle} » sera effacée de ce téléphone sans être envoyée. Ce qui en dépend (photo jointe, annulation…) aussi.`, 'Abandonner la saisie');
    if (!ok) return;
    await abandonner(proprio, op.clientOpId);
    notifier('Saisie abandonnée.');
  };

  return (
    <div className="pile ev-page">
      {confirmation.element}
      <header className="mj-entete">
        <div className="pile" style={{ gap: 0, minWidth: 0 }}>
          <h1 className="mj-h1">Envois</h1>
          <span className="secondaire mj-date masque-etroit">Saisies gardées sur ce téléphone</span>
        </div>
        {enAttente.length > 0 && (
          <Bouton variante="principal" icone={enLigne ? <CloudUpload aria-hidden /> : <CloudOff aria-hidden />} chargement={envoi} disabled={!enLigne} onClick={envoyer}
            title={enLigne ? undefined : 'Pas de réseau : l’envoi partira seul à son retour'}>
            {enLigne ? 'Envoyer maintenant' : 'Sans réseau'}</Bouton>
        )}
      </header>
      <OngletsInterventions />
      {lue && !enAttente.length && !refusees.length && (
        <EtatVide titre="Rien en attente" texte={<>Toutes vos saisies sont arrivées sur le serveur. Sans réseau, elles se rangent ici et partent seules à son retour.
          {' '}<Link to="/interventions/journee">Revenir à Ma journée</Link></>} />
      )}
      {refusees.length > 0 && (
        <Panneau titre={`Envois refusés — ${refusees.length}`}>
          <p className="petit secondaire masque-etroit" style={{ margin: 0 }}>Le serveur a refusé ces saisies : elles ne sont pas perdues. Corrigez-les, renvoyez-les (par
            exemple une fois réaffecté par le responsable), ou abandonnez-les.</p>
          <ul className="ev-liste">
            {refusees.map((op, i) => {
              // Sans saisie en attente, la première action de la première saisie refusée est l'action principale de l'écran.
              const principale = i === 0 && !enAttente.length;
              return (
              // Bon d'intervention refusé (modifié entre-temps par un collègue…) : il se règle SUR LA FICHE du bon (reprendre ou abandonner).
              op.type === 'bon' && op.bon ? (
                <CarteEnvoi key={op.clientOpId} op={op} actions={<>
                  <Link to={`/interventions/bons/${op.bon.id}`} className={`btn ${principale ? 'btn-principal' : ''}`}>Ouvrir le bon</Link>
                  <Bouton variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => jeter(op)}>Abandonner</Bouton>
                </>} />
              ) :
              <CarteEnvoi key={op.clientOpId} op={op} actions={<>
                {CORRIGEABLES[op.type] && <Bouton variante={principale ? 'principal' : 'secondaire'} icone={<Pencil aria-hidden />} onClick={() => setCorriger(op)}>Corriger</Bouton>}
                <Bouton variante={principale && !CORRIGEABLES[op.type] ? 'principal' : 'secondaire'} icone={<RotateCcw aria-hidden />} onClick={() => renvoyer(op)}>Renvoyer</Bouton>
                <Bouton variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => jeter(op)}>Abandonner</Bouton>
              </>} />
              );
            })}
          </ul>
        </Panneau>
      )}
      {enAttente.length > 0 && (
        <Panneau titre={`En attente d’envoi — ${enAttente.length}`}>
          <p className="petit secondaire" style={{ margin: 0 }}>{enLigne
            ? 'Elles partent seules : à l’ouverture, au retour du réseau et toutes les minutes. Les photos partent après les autres saisies.'
            : 'Pas de réseau : elles partiront seules dès son retour. Ne vous déconnectez pas avant (la déconnexion efface le téléphone).'}</p>
          <ul className="ev-liste">
            {enAttente.map((op) => <CarteEnvoi key={op.clientOpId} op={op} />)}
          </ul>
        </Panneau>
      )}
      {corriger && <DialogueCorriger op={corriger} fermer={() => setCorriger(null)} valider={(d) => renvoyer(corriger, d)} />}
    </div>
  );
}
